import { makeStyles, SvgIconProps, Theme, TPShipIcon } from '@freighthub/silhouette-core'
import { isAirPort, isSeaPort } from 'features/Directory'
import { TransportModeIconsMap, TTransportOrCarriageMode } from 'features/TransportPlan'
import { has } from 'lodash-es'
import { ComponentType, createElement, FC } from 'react'

import { IQuoteSheet } from '../QuoteSheetModel'
import { IQuoteOption } from '../QuoteSheetOption'
import { usePdfDownloadMode } from '../usePdfDownloadMode'
import { Descriptions } from './Descriptions'

export interface TransportProps {
  option: IQuoteOption
  quoteSheet: IQuoteSheet
}

interface StationsProps {
  option: IQuoteOption
}

interface StationProps {
  icon: TTransportModeWithShip
}

export const isOptionWithViaPortId = (option: IQuoteOption) => {
  return has(option.routeQuotes.mainQuote.rates, '0.via_ids')
}
export const isOptionWithPOD = (option: IQuoteOption) => {
  const { destination } = option.routeQuotes.mainQuote.route.journey
  return isSeaPort(destination) || isAirPort(destination)
}

const StationsIconsMap: { [key: string]: TTransportModeWithShip } = {
  sea: 'ship',
  air: 'air',
  seaAir: 'air',
  rail: 'rail',
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'flex',
  },
  visualPlan: {
    backgroundColor: theme.palette.tag.info,
    width: 150,
    position: 'relative',
  },
  line: {
    display: 'inline-block',
    position: 'absolute',
    top: '35px',
    width: 6,
    backgroundColor: theme.palette.primary.main,
    left: 'calc(50% - 6px / 2)',
    bottom: 35,
    zIndex: 0,
  },
  stations: {
    zIndex: 1,
    textAlign: 'center',
  },
  stationContainer: {
    height: 70,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  station: {
    width: 50,
    height: 50,
    backgroundColor: theme.palette.header.background,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    zIndex: 1,
    color: theme.palette.common.white,
  },
  waypoint: {
    width: 15,
    height: 15,
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
  },
}))

export const QuoteTransportPlan: FC<TransportProps> = (props) => {
  const classes = useStyles()
  return (
    <div className={classes.container}>
      <VisualPlan {...props} />
      <Descriptions {...props} />
    </div>
  )
}

const VisualPlan: FC<TransportProps> = ({ quoteSheet, option }) => {
  const classes = useStyles()
  const { isPdfDownloadMode } = usePdfDownloadMode()
  return (
    !isPdfDownloadMode && (
      <div className={classes.visualPlan}>
        <div className={classes.line} />
        {createElement(quoteSheet.request.type === 'lcl' ? LCLStations : FCLStations, { option })}
      </div>
    )
  )
}

const LCLStations: FC<StationsProps> = ({ option }) => {
  const classes = useStyles()
  // for lcl show only origin/destination and on-carriage (main run)
  return (
    <div className={classes.stations}>
      <Waypoint />
      {isOptionWithViaPortId(option) && option.mode === 'seaAir' && <Station icon="ship" />}
      {isOptionWithPOD(option) && <Station icon={StationsIconsMap[option.mode]} />}
      <Waypoint />
    </div>
  )
}

const FCLStations: FC<StationsProps> = ({ option }) => {
  const sailing = option.routeQuotes.mainQuote
  const classes = useStyles()
  return (
    <div className={classes.stations}>
      {
        // -------- PRE RUN (Pre carriage) ---------
        option.routeQuotes.preQuote && option.routeQuotes.preQuote.route && (
          <div>
            <Waypoint />
            <Station icon={option.routeQuotes.preQuote.mode} />
          </div>
        )
      }
      <Waypoint />
      {
        // -------- MAIN RUN(S) ---------
        sailing &&
          sailing.route.routeSchedule &&
          sailing.route.routeSchedule.oceanLegs.map((leg, i) => {
            let icon
            if (leg.vessel.name === 'BARGE') {
              icon = 'barge'
            } else if (leg.vessel.name === 'TRUCK') {
              icon = 'truck'
            } else {
              icon = 'ship'
            }
            return (
              <div key={i}>
                <Station icon={icon} />
                <Waypoint />
              </div>
            )
          })
      }
      {
        // --------- POST RUN (On-carriage) -----------
        option.routeQuotes.postQuote && option.routeQuotes.postQuote.route && (
          <div>
            <Station icon={option.routeQuotes.postQuote.mode} />
            <Waypoint />
          </div>
        )
      }
    </div>
  )
}

// Warning! Hack. To allow ship icon.
// TODO unify `sea` and `ship` icons. After that, remove this component.
export type TTransportModeWithShip = TTransportOrCarriageMode | 'ship'
type TTransportModeWithShipIconProps = {
  transportMode: TTransportModeWithShip
} & SvgIconProps
export const TransportModeWithShipIcon: FC<TTransportModeWithShipIconProps> = (props) => {
  const { transportMode, ...rest } = props
  const Icon: ComponentType<SvgIconProps> = transportMode === 'ship' ? TPShipIcon : TransportModeIconsMap[transportMode]
  return <Icon {...rest} />
}

const Station: FC<StationProps> = ({ icon }) => {
  const classes = useStyles()
  return (
    <div className={classes.stationContainer}>
      <div className={classes.station}>
        <TransportModeWithShipIcon transportMode={icon} />
      </div>
    </div>
  )
}
const Waypoint = () => {
  const classes = useStyles()
  return (
    <div className={classes.stationContainer}>
      <div className={classes.waypoint} />
    </div>
  )
}
